import { resolveCheckoutShippingStateCode } from '@/lib/functions/resolveCheckoutShippingDefaults';
import { DEFAULT_COUNTRY_CODE } from '@/lib/constants/countries';

import type { CheckoutFormDraft, CheckoutFormDraftTextField } from '@/lib/functions/checkoutFormDraft';
import type { Address } from '@/lib/types/address';
import type { OrderPayload } from '@/lib/types/orderPayload';
import type { User } from '@/lib/types/user';

export const CHECKOUT_ACCOUNT_PREFILL_FIELDS = [
  'shippingFirstName',
  'shippingLastName',
  'shippingPhoneNumber',
  'billingFirstName',
  'billingLastName',
  'billingPhoneNumber',
  'billingEmail',
  'billingStreet',
  'billingStreet2',
  'billingCity',
  'billingProvinceCode',
  'billingPostCode',
  'billingCountryCode',
] as const;

export type CheckoutAccountPrefillField = (typeof CHECKOUT_ACCOUNT_PREFILL_FIELDS)[number];

export type CheckoutAccountPrefillSnapshot = Partial<Record<CheckoutAccountPrefillField, string>>;

type ShippingAddressSource = {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  phoneNumber?: string | null;
  street?: string | null;
  street2?: string | null;
  city?: string | null;
  provinceCode?: string | null;
  postcode?: string | null;
  countryCode?: string | null;
};

const ESTIMATE_PLACEHOLDER_FIRST_NAME = 'Guest';
const ESTIMATE_PLACEHOLDER_LAST_NAME = 'Customer';
const ESTIMATE_PLACEHOLDER_PHONE = '5555555555';

function isEstimatePlaceholderContact(address: ShippingAddressSource): boolean {
  return (
    address.firstName === ESTIMATE_PLACEHOLDER_FIRST_NAME
    && address.lastName === ESTIMATE_PLACEHOLDER_LAST_NAME
  );
}

function isEstimatePlaceholderPhone(phone: string | null | undefined): boolean {
  if (!phone) {
    return true;
  }
  const digits = phone.replace(/\D/g, '');
  return digits === ESTIMATE_PLACEHOLDER_PHONE || digits === '5555555555';
}

function getOrderShippingContact(
  orderData: OrderPayload | null | undefined,
): Partial<Address> | null {
  const address = orderData?.fulfillment?.shippingAddress as ShippingAddressSource | undefined;
  if (!address?.street || !address.city || !address.postcode) {
    return null;
  }

  const phone = address.phoneNumber ?? address.phone ?? undefined;

  return {
    firstName: isEstimatePlaceholderContact(address) ? undefined : address.firstName ?? undefined,
    lastName: isEstimatePlaceholderContact(address) ? undefined : address.lastName ?? undefined,
    phoneNumber: isEstimatePlaceholderPhone(phone) ? undefined : phone,
  };
}

function normalizeComparableFieldValue(field: CheckoutAccountPrefillField, value: string): string {
  if (field === 'billingPhoneNumber' || field === 'shippingPhoneNumber') {
    return value.replace(/\D/g, '');
  }
  return value.trim();
}

function setPrefillValue(
  snapshot: CheckoutAccountPrefillSnapshot,
  field: CheckoutAccountPrefillField,
  value: string | null | undefined,
): void {
  if (value == null || value === '') {
    return;
  }
  snapshot[field] = value;
}

/**
 * Captures checkout field values that came from the signed-in account at load time.
 */
export function buildCheckoutAccountPrefill(
  orderData: OrderPayload | null | undefined,
  sessionUser?: User | null,
): CheckoutAccountPrefillSnapshot {
  const snapshot: CheckoutAccountPrefillSnapshot = {};
  if (!sessionUser) {
    return snapshot;
  }

  const billing = sessionUser.billingAddress;
  const shipping = sessionUser.shippingAddress;
  const orderContact = getOrderShippingContact(orderData);

  setPrefillValue(snapshot, 'billingFirstName', billing?.firstName);
  setPrefillValue(snapshot, 'billingLastName', billing?.lastName);
  setPrefillValue(snapshot, 'billingPhoneNumber', billing?.phoneNumber);
  setPrefillValue(snapshot, 'billingEmail', billing?.email ?? sessionUser.email ?? undefined);
  setPrefillValue(snapshot, 'billingStreet', billing?.street);
  setPrefillValue(snapshot, 'billingStreet2', billing?.street2 ?? undefined);
  setPrefillValue(snapshot, 'billingCity', billing?.city);
  setPrefillValue(snapshot, 'billingPostCode', billing?.postcode);

  const billingCountry = (billing?.countryCode ?? DEFAULT_COUNTRY_CODE).toUpperCase();
  setPrefillValue(snapshot, 'billingCountryCode', billingCountry);
  setPrefillValue(
    snapshot,
    'billingProvinceCode',
    resolveCheckoutShippingStateCode(
      {
        provinceCode: billing?.provinceCode,
        countryCode: billingCountry,
      },
      billingCountry === DEFAULT_COUNTRY_CODE ? 'TX' : '',
    ),
  );

  if (!orderContact?.firstName) {
    setPrefillValue(snapshot, 'shippingFirstName', shipping?.firstName);
  }
  if (!orderContact?.lastName) {
    setPrefillValue(snapshot, 'shippingLastName', shipping?.lastName);
  }
  if (!orderContact?.phoneNumber) {
    setPrefillValue(snapshot, 'shippingPhoneNumber', shipping?.phoneNumber);
  }

  return snapshot;
}

export function inferEditedAccountFields(
  draft: CheckoutFormDraft,
): CheckoutAccountPrefillField[] {
  const edited = new Set<CheckoutAccountPrefillField>(draft.editedAccountFields ?? []);
  const accountPrefill = draft.accountPrefill ?? {};

  for (const field of CHECKOUT_ACCOUNT_PREFILL_FIELDS) {
    const prefilledValue = accountPrefill[field];
    if (prefilledValue === undefined) {
      continue;
    }

    const currentValue = draft[field as CheckoutFormDraftTextField];
    if (typeof currentValue !== 'string') {
      continue;
    }

    if (
      normalizeComparableFieldValue(field, currentValue)
      !== normalizeComparableFieldValue(field, prefilledValue)
    ) {
      edited.add(field);
    }
  }

  return Array.from(edited);
}

export function getAccountSessionFieldUpdates(
  orderData: OrderPayload | null | undefined,
  sessionUser: User,
  accountPrefill: CheckoutAccountPrefillSnapshot,
  editedFields: ReadonlySet<string>,
  currentFormValues?: CheckoutFormDraft,
): {
  updates: CheckoutAccountPrefillSnapshot;
  nextAccountPrefill: CheckoutAccountPrefillSnapshot;
} {
  const latestPrefill = buildCheckoutAccountPrefill(orderData, sessionUser);
  const updates: CheckoutAccountPrefillSnapshot = {};
  const nextAccountPrefill: CheckoutAccountPrefillSnapshot = { ...accountPrefill };

  for (const field of CHECKOUT_ACCOUNT_PREFILL_FIELDS) {
    if (editedFields.has(field)) {
      continue;
    }

    if (!(field in accountPrefill)) {
      continue;
    }

    const nextValue = latestPrefill[field];
    if (nextValue === undefined) {
      continue;
    }

    const currentFormValue = currentFormValues?.[field as CheckoutFormDraftTextField];
    const displayedValue = typeof currentFormValue === 'string'
      ? currentFormValue
      : accountPrefill[field] ?? '';

    if (
      normalizeComparableFieldValue(field, nextValue)
      === normalizeComparableFieldValue(field, displayedValue)
    ) {
      continue;
    }

    updates[field] = nextValue;
    nextAccountPrefill[field] = nextValue;
  }

  return { updates, nextAccountPrefill };
}

export function applyCheckoutFormFieldUpdates(
  form: HTMLFormElement,
  updates: CheckoutAccountPrefillSnapshot,
): void {
  for (const field of CHECKOUT_ACCOUNT_PREFILL_FIELDS) {
    const value = updates[field];
    if (value === undefined) {
      continue;
    }

    const element = form.elements.namedItem(field);
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      element.value = value;
    }
  }
}

export function isCheckoutAccountPrefillField(
  field: string,
): field is CheckoutAccountPrefillField {
  return (CHECKOUT_ACCOUNT_PREFILL_FIELDS as readonly string[]).includes(field);
}
